<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import type { ScoreEntry } from '../lib/types'
import { shortHistoryDate } from '../lib/historyHelpers'

const props = defineProps<{ entries: readonly ScoreEntry[] }>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
let ro: ResizeObserver | null = null

function draw() {
  const canvas = canvasRef.value
  if (!canvas) return
  const wrap = canvas.parentElement
  const ctx = canvas.getContext('2d')
  if (!ctx || !wrap) return

  const cssW = Math.max(1, Math.floor(wrap.clientWidth || 320))
  const cssH = 160
  const dpr  = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width  = Math.floor(cssW * dpr)
  canvas.height = Math.floor(cssH * dpr)
  canvas.style.width  = cssW + 'px'
  canvas.style.height = cssH + 'px'
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

  const data = [...props.entries]
    .reverse()
    .map(entry => ({ entry, score: Number(entry.composite) }))
    .filter((item): item is { entry: ScoreEntry; score: number } => Number.isFinite(item.score))
  const scores = data.map(item => item.score)
  const W = cssW, H = cssH
  const pad = { t: 20, r: 20, b: 30, l: 40 }
  const chartW = W - pad.l - pad.r
  const chartH = H - pad.t - pad.b

  const css         = getComputedStyle(document.body)
  const mutedColor  = css.getPropertyValue('--muted').trim()  || '#5a6a7a'
  const accentColor = css.getPropertyValue('--accent').trim() || '#a07840'
  const greenColor  = css.getPropertyValue('--green').trim()  || '#2ecc71'
  const redColor    = css.getPropertyValue('--red').trim()    || '#e74c3c'

  ctx.clearRect(0, 0, W, H)

  if (scores.length < 1) {
    ctx.fillStyle = mutedColor
    ctx.textAlign = 'center'
    ctx.fillText('No scores to chart', W / 2, H / 2)
    return
  }

  const min = Math.max(0, Math.min(...scores) - 10)
  const max = Math.min(100, Math.max(...scores) + 10)
  const span = Math.max(1, max - min)
  const xScale = (i: number) => pad.l + i * (chartW / Math.max(1, scores.length - 1))
  const yScale = (v: number) => pad.t + chartH - ((v - min) / span) * chartH

  ;[75, 90].forEach(v => {
    if (v >= min && v <= max) {
      ctx.strokeStyle = v === 90 ? 'rgba(46,204,113,0.3)' : 'rgba(232,160,32,0.3)'
      ctx.lineWidth = 1
      ctx.setLineDash([4, 4])
      ctx.beginPath()
      ctx.moveTo(pad.l, yScale(v))
      ctx.lineTo(W - pad.r, yScale(v))
      ctx.stroke()
      ctx.setLineDash([])
      ctx.fillStyle = v === 90 ? 'rgba(46,204,113,0.7)' : 'rgba(232,160,32,0.7)'
      ctx.font = '10px Barlow Condensed'
      ctx.textAlign = 'right'
      ctx.fillText(String(v), pad.l - 4, yScale(v) + 4)
    }
  })

  const slotW = chartW / Math.max(1, scores.length)
  const barW = Math.max(7, Math.min(26, slotW * 0.48))
  const baseline = yScale(min)

  scores.forEach((score, i) => {
    const x = xScale(i) - barW / 2
    const y = yScale(score)
    const barH = Math.max(2, baseline - y)
    ctx.fillStyle = score >= 90 ? greenColor : score >= 75 ? accentColor : redColor
    ctx.fillRect(x, y, barW, barH)

    ctx.fillStyle = mutedColor
    ctx.font = '10px Barlow'
    ctx.textAlign = 'center'
    ctx.fillText(shortHistoryDate(data[i]!.entry), xScale(i), H - 5)
  })
}

watch(() => props.entries, () => { draw() }, { deep: true })

onMounted(() => {
  draw()
  if (canvasRef.value?.parentElement) {
    ro = new ResizeObserver(() => { draw() })
    ro.observe(canvasRef.value.parentElement)
  }
})

onUnmounted(() => {
  ro?.disconnect()
})
</script>

<template>
  <div class="hist-bar-wrap">
    <canvas ref="canvasRef"></canvas>
  </div>
</template>
